<template>
  <form @submit.prevent="updateHomework()" action="">
    <ion-row>
      <ion-col size="12">
        <ion-list>
          <ion-item v-if="validationError" lines="none">
            <ion-label className="ion-text-wrap">
              <ion-text color="danger">
                <p v-for="error of v$.$errors" :key="error.$uid">
                  <ion-icon
                    :ios="alertCircleOutline"
                    :md="alertCircleSharp"
                    size="small"
                    style="vertical-align: middle"
                  ></ion-icon
                  >{{ error.$property }}: {{ error.$message }}
                </p></ion-text
              >
            </ion-label>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Class</ion-label>
            <ion-input type="text" v-model="class_name" disabled></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Subject</ion-label>
            <ion-input type="text" v-model="subject_name" disabled></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Chapter</ion-label>
            <ion-input type="text" v-model="chapter_name" disabled></ion-input>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.homework_from_date.$error }">
            <ion-label position="floating">Start date</ion-label>
            <ion-datetime
              type="text"
              v-model="homework_from_date"
            ></ion-datetime>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.homework_to_date.$error }">
            <ion-label position="floating">Completion date</ion-label>
            <ion-datetime type="text" v-model="homework_to_date"></ion-datetime>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.description.$error }">
            <ion-label position="floating">Description</ion-label>
            <ion-textarea
              type="text"
              v-model="description"
              rows="2"
            ></ion-textarea>
          </ion-item>
        </ion-list>
      </ion-col>
    </ion-row>
    <ion-row>
      <ion-col>
        <ion-button type="submit" expand="full" class="ion-margin-vertical">
          Save
        </ion-button>
      </ion-col>
    </ion-row>
  </form>
</template>

<script>
import {
  IonRow,
  IonCol,
  IonList,
  IonLabel,
  IonItem,
  IonIcon,
  IonButton,
  IonInput,
  IonTextarea,
  IonDatetime,
  IonText,
  loadingController,
  alertController,
} from "@ionic/vue";

import { alertCircleOutline, alertCircleSharp } from "ionicons/icons";

import { required, maxLength, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import moment from "moment";

export default {
  props: {
    resource: {
      type: Object,
      required: true,
    },
  },

  emits: ["closeModal"],
  components: {
    IonRow,
    IonCol,
    IonList,
    IonLabel,
    IonItem,
    IonIcon,
    IonButton,
    IonInput,
    IonTextarea,
    IonDatetime,
    IonText,
  },
  data() {
    return {
      class_name:
        this.resource.section_standard.standard.name +
        " - " +
        this.resource.section_standard.section.name,
      subject_name: this.resource.subject.name,
      chapter_name: this.resource.chapter.name,

      id: this.resource.id,
      description: this.resource.description,
      homework_from_date: this.resource.homework_from_date,
      homework_to_date: this.resource.homework_to_date,

      validationError: null,

      alertCircleOutline,
      alertCircleSharp,

      xhrError: {
        header: null,
        description: null,
      },
    };
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      homework_from_date: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage(
          "maximum 255 characters",
          maxLength(255)
        ),
      },
      homework_to_date: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage(
          "maximum 255 characters",
          maxLength(255)
        ),
        // Custom Validator
        minValue: helpers.withMessage(
          "cannot be less than from date",
          function minValue(val) {
            return new Date(val) >= new Date(this.homework_from_date);
          }
        ),
      },

      description: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage(
          "maximum 255 characters",
          maxLength(255)
        ),
      },
    };
  },

  methods: {
    setModalState(state) {
      this.modalState = state;
    },

    loader(message) {
      const loading = loadingController.create({
        message: message,
        backdropDismiss: false,
      });

      return loading;
    },

    alerter(header, message, buttons = ["close"]) {
      const alert = alertController.create({
        header: header,
        message: message,
        buttons: buttons,
      });

      return alert;
    },

    setErrorResponse(error) {
      if (error.response) {
        this.xhrError.header = error.response.data.header;
        this.xhrError.message = error.response.data.message;
      } else if (error.request) {
        console.log(error.request);
        this.xhrError.header = error.message || error.request;
        this.xhrError.message = "Please check your connection and try again";
      } else {
        this.xhrError.header = error.message;
        this.xhrError.message = "Something went wrong. Try again later";
      }
    },

    async updateHomework() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.validationError = true;
        return;
      }

      let data = {
        id: this.id,
        homework_from_date: moment(this.homework_from_date).format(
          "YYYY-MM-DD"
        ),
        homework_to_date: moment(this.homework_to_date).format("YYYY-MM-DD"),
        description: this.description,
      };

      const spinner = await this.loader("Saving...");
      spinner.present();

      try {
        await this.$store.dispatch("homework/updateResource", data);
        spinner.dismiss();

        this.name = null;
        this.$emit("closeModal");
      } catch (error) {
        spinner.dismiss();

        this.setErrorResponse(error);

        const alertDialog = await this.alerter(
          this.xhrError.header,
          this.xhrError.message
        );

        alertDialog.present();

        this.password = null;
        this.validationError = null;
      }
    },
  },
};
</script>